// 🔐 Login — HUD inmersivo estilo "Arc Reactor". Escena oscura (el reactor necesita
// oscuridad para brillar) con el ACENTO teñido por el HUE del tema activo (cambiar de
// tema cambia el color del glow). Conserva toda la funcionalidad: useLogin, validaciones,
// i18n, selectores de tema/idioma, error/carga, "olvidé contraseña" y modal LOPDP.
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLogin } from '@/lib/hooks';
import { MfaPaso } from '@/components/auth/MfaPaso';
import { useTranslation } from '@/lib/hooks/useTranslation';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { authAPI, esDesafioMfa, loginSlugStore, type LoginBranding, type MfaDesafio } from '@/lib/api/auth.api';
import { useThemeStore } from '@/stores/theme.store';
import { THEMES } from '@/lib/constants/themes';
import { LanguageSelector } from '@/components/settings/LanguageSelector';
import { ThemeSelector } from '@/components/settings/ThemeSelector';

// Iniciales (2-3 letras) para el monograma de respaldo cuando la empresa no tiene
// logo o este falla al cargar. Nunca deja un ícono roto ni un hueco vacío.
function inicialesDe(nombre: string): string {
  const palabras = nombre
    .trim()
    .split(/\s+/)
    .filter((w) => /[a-zA-Z0-9ÁÉÍÓÚÑáéíóúñ]/.test(w));
  if (palabras.length === 0) return '?';
  if (palabras.length === 1) return palabras[0].slice(0, 3).toUpperCase();
  return palabras.slice(0, 3).map((w) => w[0]!.toUpperCase()).join('');
}

type BrandingState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'notfound' }
  | { status: 'ok'; nombre: string; logo: string | null; fondo: string | null };

// Los colores del tema son tripletes HSL ("174 62% 28%"). Se derivan variantes vívidas
// para el HUD (más luminosas, para que brillen sobre la escena oscura).
function hue(triplet: string) {
  const [h = '200', s = '80%'] = String(triplet).split(/\s+/);
  return { h, s };
}

export function LoginK2E() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const { mutate: login, isPending, error } = useLogin();
  const { t } = useTranslation();
  const { currentThemeId } = useThemeStore();
  const currentTheme = THEMES.find((th) => th.id === currentThemeId) || THEMES[0];

  const { h, s } = hue(currentTheme.colors.primary);
  const ACC = `hsl(${h} ${s} 62%)`;                         // acento vívido (líneas, núcleo, glow)
  const ACCA = (a: number) => `hsl(${h} ${s} 62% / ${a})`;   // acento con alfa
  const SCENE = `radial-gradient(circle at 50% 18%, hsl(${h} 45% 12%) 0%, #060a10 55%, #03040a 100%)`;

  const [formData, setFormData] = useState({ username: '', password: '' });
  // Vista de la tarjeta de login: el aviso de privacidad y "olvidé mi contraseña" se
  // muestran integrados dentro de la misma tarjeta (sin ventanas modales), no como
  // superposiciones aparte.
  const [vista, setVista] = useState<'login' | 'privacidad' | 'olvide' | 'mfa'>('login');
  const [desafio, setDesafio] = useState<MfaDesafio | null>(null);
  const [errors, setErrors] = useState<{ username?: string; password?: string }>({});

  // --- Dominio propio: el host puede identificar a la empresa (script 904) ---
  // Con `empresas.login_host` configurado, entrar a `zm2.drpro.com.ec` basta:
  // no hace falta el slug opaco en la URL. La URL SIEMPRE manda — si la ruta
  // trae /login/:slug ni se consulta el host. Y el host es solo ruteo: el
  // tenant que viaja al backend sigue siendo el slug, y el empresa_id del JWT
  // sale de la fila del usuario (regla de oro de 586/587).
  const [slugHost, setSlugHost] = useState<string | null>(null);
  const [hostResuelto, setHostResuelto] = useState(!!slug);
  const slugEfectivo = slug || slugHost;

  // --- "¿Olvidaste tu contraseña?" (panel integrado) — respuesta siempre neutra:
  // nunca revela si el correo existe. El tenant viene del slug de la URL; si el
  // login está pelado (sin slug) se pide el código de acceso de la empresa.
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotTenant, setForgotTenant] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const solicitarReset = useMutation({
    mutationFn: () => authAPI.solicitarReset(forgotEmail.trim(), slugEfectivo || forgotTenant.trim() || undefined),
    onSuccess: () => setForgotSent(true),
    onError: (e) => toast.error(getErrorMessage(e) || t('auth.resetPassword.networkError')),
  });

  const abrirForgot = () => {
    setForgotEmail('');
    setForgotTenant('');
    setForgotSent(false);
    setVista('olvide');
  };

  const puedeEnviarForgot = forgotEmail.trim().length > 0 && (!!slugEfectivo || forgotTenant.trim().length > 0);

  const handleForgotSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!puedeEnviarForgot || solicitarReset.isPending) return;
    solicitarReset.mutate();
  };

  // --- Branding multi-tenant por slug (Fase 1: solo marca + ruteo + memoria) ---
  const [branding, setBranding] = useState<BrandingState>(slug ? { status: 'loading' } : { status: 'idle' });
  const [logoError, setLogoError] = useState(false);
  const [fondoError, setFondoError] = useState(false);
  const [codigoEmpresa, setCodigoEmpresa] = useState('');
  const [slugRecordado, setSlugRecordado] = useState<string | null>(() => (slug ? null : loginSlugStore.get()));

  useEffect(() => {
    if (!slug) return;
    let vivo = true;
    setBranding({ status: 'loading' });
    setLogoError(false);
    setFondoError(false);
    authAPI
      .getBranding(slug)
      .then((data: LoginBranding | null) => {
        if (!vivo) return;
        if (data && data.nombre) {
          setBranding({ status: 'ok', nombre: data.nombre, logo: data.logo ?? null, fondo: data.fondo ?? null });
          loginSlugStore.set(slug);
        } else {
          setBranding({ status: 'notfound' });
        }
      })
      .catch(() => {
        if (vivo) setBranding({ status: 'notfound' });
      });
    return () => {
      vivo = false;
    };
  }, [slug]);

  // ¿Este dominio pertenece a alguna empresa? Se resuelve una sola vez, y
  // AHORA TAMBIÉN CON SLUG EN LA URL (script 905). Si el host no está asignado
  // (localhost, una IP, un entorno sin el 904) se cae al login de siempre.
  //
  // Dos casos, según venga o no slug:
  //
  // · Sin slug — es el 904 de siempre: el dominio elige la empresa y pinta su
  //   marca. Mientras tanto no se pinta ni la marca ni el bloque de "código de
  //   empresa", para que no parpadee el formulario equivocado.
  //
  // · Con slug — el 905 acota aquella regla de que "la URL manda sobre el
  //   host": sigue mandando, pero NO para colarse en otra empresa. Si el
  //   dominio es de una empresa y el slug es de OTRA, se devuelve al usuario al
  //   login de SU dominio, sin mensaje de error: lo normal es que haya llegado
  //   por un enlace viejo o mal copiado, no que esté intentando nada. La BD
  //   rechaza igual ese login (guard del 905), esto solo evita que lo teclee.
  useEffect(() => {
    let vivo = true;
    authAPI
      .getBrandingHost(window.location.hostname)
      .then((data) => {
        if (!vivo || !data) return;

        if (slug) {
          if (slug !== data.slug) {
            // El recordado es de otro dominio: borrarlo o reaparecería como
            // "entrar como ..." en la empresa que no toca.
            loginSlugStore.clear();
            navigate(`/login/${data.slug}`, { replace: true });
          }
          return;
        }

        setSlugHost(data.slug);
        setBranding({ status: 'ok', nombre: data.nombre, logo: data.logo ?? null, fondo: data.fondo ?? null });
        // No se guarda en loginSlugStore: el dominio ya lo resuelve en cada
        // visita, y recordarlo contaminaría el /login pelado de otro host.
      })
      .finally(() => {
        if (vivo) setHostResuelto(true);
      });
    return () => {
      vivo = false;
    };
  }, [slug, navigate]);

  // Fondo personalizado de la empresa (si lo configuró y cargó bien). Es puramente
  // decorativo: nunca bloquea el render del formulario ni el login en sí.
  const fondoUrl = branding.status === 'ok' ? branding.fondo : null;
  const mostrarFondo = !!fondoUrl && !fondoError;

  const olvidarEmpresa = () => {
    loginSlugStore.clear();
    setSlugRecordado(null);
  };

  const irAEmpresaCodigo = (e: React.FormEvent) => {
    e.preventDefault();
    const code = codigoEmpresa.trim().toLowerCase();
    if (!code) return;
    navigate(`/login/${code}`);
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};
    if (!formData.username.trim()) newErrors.username = t('auth.validation.usernameRequired');
    if (!formData.password) newErrors.password = t('auth.validation.passwordRequired');
    else if (formData.password.length < 6) newErrors.password = t('auth.validation.passwordMinLength', { min: 6 });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    login(
      // El tenant viene del slug de la URL o, si no hay, del que resolvió el
      // dominio (NUNCA del loginSlugStore): el login va a la empresa que el
      // usuario está viendo en pantalla. Sin ninguno de los dos no se manda
      // tenant y el backend usa el fallback global.
      { username: formData.username, password: formData.password, tenant: slugEfectivo || undefined },
      {
        onSuccess: (data) => {
          // Usuario con MFA: la clave fue correcta pero falta el código de la app.
          if (esDesafioMfa(data)) { setDesafio(data); setVista('mfa'); return; }
          navigate('/');
        },
        onError: (err) => console.error('Error en login:', err),
      }
    );
  };

  const handleChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const hudField = (field: keyof typeof formData, type: 'text' | 'password', label: string) => (
    <div className="mb-5">
      <label htmlFor={field} className="mb-2 block text-[9px] uppercase tracking-[0.28em]" style={{ color: ACC }}>{label}</label>
      <div className="arc-inrow flex items-center gap-2 border-b pb-1.5 transition-colors" style={{ borderColor: ACCA(0.35) }}>
        <span className="select-none text-sm" style={{ color: ACC, textShadow: `0 0 8px ${ACCA(0.8)}` }}>&gt;</span>
        <input
          id={field} type={type} value={formData[field]} onChange={handleChange(field)}
          disabled={isPending} autoFocus={field === 'username'}
          autoComplete={field === 'username' ? 'username' : 'current-password'}
          placeholder={type === 'password' ? '••••••••••' : label.toLowerCase()}
          className="w-full border-0 bg-transparent p-0 text-sm tracking-wide text-[#e9f4fb] placeholder:text-white/25 focus:outline-none focus:ring-0 disabled:opacity-60"
        />
      </div>
      {errors[field] && <p className="mt-1.5 text-xs text-red-400">{errors[field]}</p>}
    </div>
  );

  return (
    <div className="relative min-h-screen overflow-hidden font-mono" style={{ background: SCENE, ['--acc' as string]: ACC } as React.CSSProperties}>
      <style>{`
        @keyframes arcspin{to{transform:rotate(360deg)}}
        @keyframes arcpulse{0%,100%{opacity:.55;transform:scale(1)}50%{opacity:1;transform:scale(1.05)}}
        @keyframes archalo{0%,100%{opacity:.30;transform:scale(.9)}50%{opacity:.65;transform:scale(1.1)}}
        @keyframes arcscan{0%{top:2%}100%{top:98%}}
        @keyframes arcshimmer{0%{transform:translateX(-130%)}55%,100%{transform:translateX(240%)}}
        .arc-ring1{animation:arcspin 9s linear infinite;transform-origin:75px 75px}
        .arc-ring2{animation:arcspin 6s linear infinite reverse;transform-origin:75px 75px}
        .arc-sweep{animation:arcspin 2.6s linear infinite;transform-origin:75px 75px}
        .arc-core{animation:arcpulse 2.4s ease-in-out infinite;transform-origin:center}
        .arc-radar{animation:arcspin 4.5s linear infinite}
        .arc-halo{animation:archalo 3s ease-in-out infinite}
        .arc-scanline{animation:arcscan 3.4s linear infinite}
        .arc-shimmer{animation:arcshimmer 4s ease-in-out infinite}
        .arc-inrow:focus-within{border-color:var(--acc)!important;box-shadow:0 6px 12px -8px var(--acc)}
        @media (prefers-reduced-motion: reduce){
          .arc-ring1,.arc-ring2,.arc-sweep,.arc-core,.arc-radar,.arc-halo,.arc-scanline,.arc-shimmer{animation:none}
        }
      `}</style>

      {/* Fondo personalizado de la empresa (si tiene), con velo para mantener el HUD legible.
          Sin fondo (o si falla la carga) se conserva el look "Arc Reactor" de siempre (SCENE). */}
      {mostrarFondo && (
        <>
          <img
            src={fondoUrl as string} alt="" aria-hidden
            onError={() => setFondoError(true)}
            className="absolute inset-0 z-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 z-0 bg-black/55" />
        </>
      )}

      {/* Grid + viñeta para dar profundidad de "cabina" */}
      <div className="pointer-events-none absolute inset-0" style={{
        backgroundImage: `linear-gradient(${ACCA(0.05)} 1px, transparent 1px), linear-gradient(90deg, ${ACCA(0.05)} 1px, transparent 1px)`,
        backgroundSize: '40px 40px', maskImage: 'radial-gradient(circle at 50% 40%, #000 0%, transparent 75%)',
      }} />
      <div className="pointer-events-none absolute inset-0" style={{ boxShadow: 'inset 0 0 240px 40px rgba(0,0,0,.85)' }} />

      {/* Selectores (funcionalidad intacta), en tono claro para la escena oscura */}
      <div className="absolute right-4 top-4 z-20 flex items-center gap-2 text-slate-200 [&_button]:text-slate-200">
        <ThemeSelector />
        <LanguageSelector />
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: 'easeOut' }} className="w-full max-w-md"
        >
          <div className="relative rounded-md border px-8 pb-7 pt-9 backdrop-blur-xl"
            style={{
              borderColor: ACCA(0.4),
              background: `linear-gradient(180deg, hsl(${h} 40% 9% / .55), rgba(4,7,12,.72))`,
              boxShadow: `0 0 90px ${ACCA(0.18)}, 0 30px 60px -20px rgba(0,0,0,.8), inset 0 0 60px ${ACCA(0.06)}`,
            }}>
            {/* Esquinas HUD */}
            {(['tl', 'tr', 'bl', 'br'] as const).map((c) => (
              <span key={c} className="absolute h-4 w-4" style={{
                top: c[0] === 't' ? 8 : undefined, bottom: c[0] === 'b' ? 8 : undefined,
                left: c[1] === 'l' ? 8 : undefined, right: c[1] === 'r' ? 8 : undefined,
                borderTop: c[0] === 't' ? `2px solid ${ACC}` : undefined,
                borderBottom: c[0] === 'b' ? `2px solid ${ACC}` : undefined,
                borderLeft: c[1] === 'l' ? `2px solid ${ACC}` : undefined,
                borderRight: c[1] === 'r' ? `2px solid ${ACC}` : undefined,
                filter: `drop-shadow(0 0 4px ${ACCA(0.7)})`,
              }} />
            ))}
            <span className="arc-scanline pointer-events-none absolute left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${ACC}, transparent)` }} />

            <div className="mb-5 text-center text-[10px] uppercase tracking-[0.38em]" style={{ color: ACCA(0.85) }}>
              Sistema integrado
            </div>

            {vista === 'login' && (
            <>
            {/* Branding de empresa (por slug en la URL o por dominio propio) — nunca bloquea el formulario */}
            {slugEfectivo && (
              <div className="mb-5 flex items-center justify-center gap-3 rounded-md border px-3 py-2.5"
                style={{ borderColor: ACCA(0.25), background: `hsl(${h} 30% 10% / .4)` }}>
                {branding.status === 'ok' ? (
                  <>
                    {branding.logo && !logoError ? (
                      <img src={branding.logo} alt={branding.nombre} onError={() => setLogoError(true)}
                        className="h-9 w-9 shrink-0 rounded-sm bg-white/5 object-contain" />
                    ) : (
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm text-[11px] font-bold"
                        style={{ background: ACCA(0.18), color: ACC, border: `1px solid ${ACCA(0.4)}` }}>
                        {inicialesDe(branding.nombre)}
                      </div>
                    )}
                    <span className="truncate text-sm font-medium text-[#eaf4fb]">{branding.nombre}</span>
                  </>
                ) : branding.status === 'notfound' ? (
                  <>
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm text-[11px] font-bold"
                      style={{ background: ACCA(0.18), color: ACC, border: `1px solid ${ACCA(0.4)}` }}>
                      K²E
                    </div>
                    <span className="truncate text-xs text-amber-300/90">{t('auth.brandingNoIdentificada')}</span>
                  </>
                ) : (
                  <>
                    <div className="h-9 w-9 shrink-0 animate-pulse rounded-sm" style={{ background: ACCA(0.12) }} />
                    <div className="h-3 w-28 animate-pulse rounded" style={{ background: ACCA(0.12) }} />
                  </>
                )}
              </div>
            )}

            {/* Reactor con halo + radar + núcleo pulsante */}
            <div className="relative mx-auto mb-6 h-[140px] w-[140px]">
              <div className="arc-halo absolute inset-0 rounded-full" style={{ background: `radial-gradient(circle, ${ACCA(0.55)} 0%, transparent 65%)`, filter: 'blur(6px)' }} />
              <div className="arc-radar absolute inset-2 rounded-full" style={{ background: `conic-gradient(from 0deg, transparent 0 280deg, ${ACCA(0.35)} 340deg, transparent 360deg)` }} />
              <svg viewBox="0 0 150 150" className="absolute inset-0 h-full w-full" style={{ filter: `drop-shadow(0 0 6px ${ACCA(0.6)})` }}>
                <circle className="arc-ring1" cx="75" cy="75" r="68" fill="none" stroke={ACCA(0.55)} strokeWidth="1" strokeDasharray="4 6" />
                <circle className="arc-ring2" cx="75" cy="75" r="55" fill="none" stroke={ACCA(0.75)} strokeWidth="1" strokeDasharray="1 5" />
                <g className="arc-sweep"><line x1="75" y1="75" x2="75" y2="9" stroke={ACC} strokeWidth="1.6" opacity="0.8" /></g>
                <g className="arc-core">
                  <circle cx="75" cy="75" r="26" fill={`hsl(${h} 55% 9%)`} stroke={ACC} strokeWidth="2" style={{ filter: `drop-shadow(0 0 10px ${ACC})` }} />
                  <circle cx="75" cy="75" r="10" fill={ACC} style={{ filter: `drop-shadow(0 0 12px ${ACC})` }} />
                </g>
                <g stroke={ACC} strokeWidth="2">
                  <line x1="75" y1="49" x2="75" y2="61" /><line x1="75" y1="89" x2="75" y2="101" />
                  <line x1="49" y1="75" x2="61" y2="75" /><line x1="89" y1="75" x2="101" y2="75" />
                </g>
              </svg>
            </div>

            <h1 className="text-center text-[16px] font-semibold uppercase tracking-[0.22em] text-[#eaf4fb]" style={{ textShadow: `0 0 18px ${ACCA(0.5)}` }}>
              {t('common.welcome')}
            </h1>
            <p className="mb-7 text-center text-[10px] uppercase tracking-[0.18em]" style={{ color: ACCA(0.9) }}>
              {t('auth.loginSubtitle')}
            </p>

            <form onSubmit={handleSubmit}>
              {hudField('username', 'text', t('auth.username'))}
              {hudField('password', 'password', t('auth.password'))}

              {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                  className="mb-3 rounded border border-red-500/40 bg-red-500/10 p-3">
                  <p className="text-sm text-red-300">{getErrorMessage(error)}</p>
                </motion.div>
              )}

              <button type="submit" disabled={isPending}
                className="relative mt-2 flex w-full items-center justify-center gap-2 overflow-hidden rounded-sm py-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#04121a] transition hover:brightness-110 disabled:opacity-70"
                style={{ background: `linear-gradient(180deg, hsl(${h} ${s} 58%), hsl(${h} ${s} 44%))`, boxShadow: `0 0 26px ${ACCA(0.55)}, inset 0 0 12px rgba(255,255,255,.22)` }}>
                <span className="arc-shimmer pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 skew-x-[-20deg] bg-white/35" />
                {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {isPending ? t('auth.loggingIn') : t('auth.login')}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button type="button" className="text-xs text-slate-400 transition-colors hover:text-slate-200"
                onClick={abrirForgot}>
                {t('auth.forgotPassword')}
              </button>
            </div>

            {/* Solo en /login pelada y en un dominio SIN empresa asignada: memoria
                de tenant + código de acceso. Si el dominio ya identificó a la
                empresa, pedir un código sobraría y confundiría. */}
            {hostResuelto && !slugEfectivo && (
              <div className="mt-5 space-y-3">
                {slugRecordado && (
                  <div className="rounded-md border px-3 py-2.5 text-center"
                    style={{ borderColor: ACCA(0.3), background: `hsl(${h} 30% 10% / .4)` }}>
                    <button type="button" onClick={() => navigate(`/login/${slugRecordado}`)}
                      className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: ACC }}>
                      {t('auth.entrarAEmpresa')}
                    </button>
                    <div>
                      <button type="button" onClick={olvidarEmpresa}
                        className="mt-1 text-[10px] text-slate-500 underline-offset-2 hover:text-slate-300 hover:underline">
                        {t('auth.equipoCompartidoOlvidar')}
                      </button>
                    </div>
                  </div>
                )}

                <form onSubmit={irAEmpresaCodigo} className="border-t pt-3" style={{ borderColor: ACCA(0.15) }}>
                  <label htmlFor="codigoEmpresa" className="mb-1.5 block text-[9px] uppercase tracking-[0.22em]" style={{ color: ACCA(0.7) }}>
                    {t('auth.empresaCodigo')}
                  </label>
                  <div className="flex gap-2">
                    <input id="codigoEmpresa" type="text" value={codigoEmpresa}
                      onChange={(e) => setCodigoEmpresa(e.target.value)}
                      placeholder="ej. mi-empresa-ab12"
                      className="w-full rounded-sm border bg-transparent px-2 py-1.5 text-xs text-[#e9f4fb] placeholder:text-white/20 focus:outline-none"
                      style={{ borderColor: ACCA(0.25) }} />
                    <button type="submit"
                      className="shrink-0 rounded-sm border px-3 text-[10px] uppercase tracking-[0.14em] transition hover:brightness-125"
                      style={{ borderColor: ACCA(0.4), color: ACC }}>
                      {t('auth.empresaCodigoIr')}
                    </button>
                  </div>
                  <p className="mt-1 text-[10px] text-slate-500">{t('auth.empresaCodigoHelp')}</p>
                </form>
              </div>
            )}

            <div className="mt-5 flex justify-between text-[9px] uppercase tracking-[0.12em]" style={{ color: ACCA(0.6) }}>
              <span>{t('footer.version')} 3.0.0</span>
              <button type="button" className="uppercase tracking-[0.12em] underline-offset-2 hover:underline" onClick={() => setVista('privacidad')}>
                Aviso de privacidad
              </button>
            </div>
            </>
            )}

            {vista === 'mfa' && desafio && (
              <MfaPaso desafio={desafio} acc={ACC} onListo={() => navigate('/')}
                onCancelar={() => { setDesafio(null); setFormData((f) => ({ ...f, password: '' })); setVista('login'); }} />
            )}

            {vista === 'privacidad' && (
              <div className="text-sm">
                <h2 className="mb-3 text-center text-[13px] font-semibold uppercase tracking-[0.2em] text-[#eaf4fb]">Aviso de privacidad</h2>
                <p className="mb-2 text-slate-300">
                  En cumplimiento de la Ley Orgánica de Protección de Datos Personales del Ecuador, esta plataforma trata
                  los datos de acceso (usuario y credenciales) con la única finalidad de autenticar y gestionar su sesión.
                  La organización con la que usted se relaciona actúa como responsable del tratamiento de sus datos.
                </p>
                <p className="mb-2 text-slate-300">
                  Una vez que inicie sesión podrá consultar el aviso de privacidad detallado de su organización y ejercer
                  sus derechos de acceso, rectificación, eliminación, oposición, portabilidad y suspensión, así como
                  retirar su consentimiento, desde el portal.
                </p>
                <p className="text-slate-300">
                  Para ejercer sus derechos antes de ingresar, contacte al responsable del tratamiento de su organización.
                </p>
                <div className="mt-4 text-right">
                  <Button type="button" variant="outline" onClick={() => setVista('login')}>Volver</Button>
                </div>
              </div>
            )}

            {vista === 'olvide' && (
              <div className="text-sm">
                <h2 className="mb-1 text-center text-[13px] font-semibold uppercase tracking-[0.2em] text-[#eaf4fb]">{t('auth.resetPassword.title')}</h2>

                {!forgotSent ? (
                  <>
                    <p className="mb-4 text-slate-300">{t('auth.resetPassword.subtitle')}</p>
                    <form onSubmit={handleForgotSubmit} className="space-y-3">
                      <div>
                        <label htmlFor="forgotEmail" className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-slate-400">
                          {t('auth.resetPassword.emailLabel')}
                        </label>
                        <input
                          id="forgotEmail" type="email" required autoFocus
                          value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                          placeholder={t('auth.resetPassword.emailPlaceholder')} autoComplete="email"
                          disabled={solicitarReset.isPending}
                          className="w-full rounded-sm border bg-transparent px-2 py-1.5 text-sm text-[#e9f4fb] placeholder:text-white/20 focus:outline-none disabled:opacity-60"
                          style={{ borderColor: ACCA(0.3) }}
                        />
                      </div>

                      {!slug && (
                        <div>
                          <label htmlFor="forgotTenant" className="mb-1 block text-[10px] uppercase tracking-[0.18em] text-slate-400">
                            {t('auth.resetPassword.tenantLabel')}
                          </label>
                          <input
                            id="forgotTenant" type="text" required
                            value={forgotTenant} onChange={(e) => setForgotTenant(e.target.value)}
                            placeholder={t('auth.resetPassword.tenantPlaceholder')}
                            disabled={solicitarReset.isPending}
                            className="w-full rounded-sm border bg-transparent px-2 py-1.5 text-sm text-[#e9f4fb] placeholder:text-white/20 focus:outline-none disabled:opacity-60"
                            style={{ borderColor: ACCA(0.3) }}
                          />
                          <p className="mt-1 text-[10px] text-slate-500">{t('auth.resetPassword.tenantHelp')}</p>
                        </div>
                      )}

                      <div className="mt-4 flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setVista('login')} disabled={solicitarReset.isPending}>
                          {t('auth.resetPassword.cancel')}
                        </Button>
                        <Button type="submit" disabled={!puedeEnviarForgot || solicitarReset.isPending} isLoading={solicitarReset.isPending}>
                          {solicitarReset.isPending ? t('auth.resetPassword.sending') : t('auth.resetPassword.submit')}
                        </Button>
                      </div>
                    </form>
                  </>
                ) : (
                  <>
                    <p className="mb-4 text-slate-300">{t('auth.resetPassword.neutralMessage')}</p>
                    <div className="text-right">
                      <Button type="button" variant="outline" onClick={() => setVista('login')}>{t('auth.resetPassword.close')}</Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
